// React
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// MUI
import { Grid, Box, Button, Container, Divider } from "@mui/material";
import Iconify from "../../../components/iconify/Iconify";
// Constants
import EntityConstants from "../../../configs/EntityConstants";
import MessageContants from "../../../configs/MessageContants";
// Service
import { get, post, put, del, gen_link } from "../../../services/FetchHelper";
import SpinService from "../../../services/SpinService";
// Utils
import GoDialog from "../../../common/GoDialog/GoDialog";
import {
    requiredFormFieldMapper,
    disabledFormFieldMapper,
    notDisabled,
    formConfig,
    months,
} from "./Receipt.utils";
// Styles
import { StyledDrawerForm } from "../../../configs/GoStyles";
//Third party
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import GoForm from "../../../components/Inputs/GoForm";
import PermissionConstants from "../../../configs/PermissionConstants";
import usePermissions from "../../../hooks/usePermissions";
import ReceiptLines from "./ReceiptLines";

const ReceiptHeader = ({ id, onClose, studentId, schoolId }) => {
    const permissions = usePermissions();
    const spinService = SpinService();
    const [receipt_header, setReceiptHeader] = useState(
        formConfig["School Transport"].state
    );
    const [schema, setSchema] = useState({});
    const [formFields, setFormFields] = useState([]);
    const [confirm, setConfirm] = useState(false);

    const [receiptLines, setReceiptLines] = useState([]);
    const [dependencies, setDependencies] = useState({
        schools: [],
        students: [],
        accounts: [],
        fee_types: [],
        cheque_types: [],
        debit_ledgers: [],
        voucher_types: [],
        transport_types: [],
        terms: [],
        academic_years: [],
        classes: [],
        zones: [],
        areas: [],
        mode_of_pays: [],
        bank_details: [],
        invoice_headers: [],
        unpaid_months: [],
    });

    const attachOptions = (name, options) => {
        setFormFields((prevFormFields) =>
            prevFormFields.map((field) =>
                field.name === name &&
                    (field.type === "select" ||
                        field.type === "autocomplete" ||
                        field.type === "multiselect")
                    ? { ...field, options: options }
                    : field
            )
        );
    };

    const makeRequired = (name, required) => {
        setFormFields((prevFormFields) =>
            prevFormFields.map((field) =>
                field.name === name ? { ...field, required: required } : field
            )
        );
    };

    const makeDisabled = (name, disabled) => {
        setFormFields((prevFormFields) =>
            prevFormFields.map((field) =>
                field.name === name ? { ...field, disabled: disabled } : field
            )
        );
    };

    const handleClose = async (res) => {
        if (res) {
            spinService.on();
            const response = await del(
                EntityConstants.RECEIPT_HEADERS + `/${receipt_header.id}`
            );
            if (response.status === 200) {
                toast.success(MessageContants.RECEIPT_HEADER_DELETED);
                onClose();
            }
            spinService.off();
        }
        setConfirm(false);
    };

    const receiptHeaderChangeHandler = (key, value) => {
        if (key === "receipt_status" && typeof value !== "boolean") {
            value = value === "true" ? false : true;
        } else if (key === "receipt_status" && typeof value === "boolean") {
            value = value ? false : true;
        }
        setReceiptHeader((prevState) => {
            return { ...prevState, [key]: value };
        });
    };

    const handleValueChange = async (e, values) => {
        receiptHeaderChangeHandler(e.target.name, e.target.value);
        return;
    };

    const handlePdfDownloadChange = async () => {
        const response = await fetch(
            gen_link(`${EntityConstants.PDF_RECEIPT_DOWNLOAD}/${receipt_header.id}`)
        );
        const pdfBlob = await response.blob();
        const pdfBlobUrl = URL.createObjectURL(pdfBlob);
        const previewWindow = window.open(pdfBlobUrl, "_blank");
    };

    const handleStatusUpdateHandler = (receipt_header) => {
        handleSubmit(receipt_header);
    };

    const fetchReceiptLines = async (receipt_header_id) => {
        spinService.on();
        const response = await get(
            EntityConstants.RECEIPT_HEADERS + `/${receipt_header_id}`
        );
        const data = await response.json();
        setReceiptLines(data.receipt_lines);
        spinService.off();
    };

    const monthValidation = (values) => {
        if (values.fee_type !== "School Transport") {
            return true;
        }
        let selectedMonths = dependencies.unpaid_months
            .filter((mon) => values.months.includes(mon.month))
            .map((mon) => new Date(mon.invoice_date));
        let unSelectedMonths = dependencies.unpaid_months
            .filter((mon) => !values.months.includes(mon.month))
            .map((mon) => new Date(mon.invoice_date));
        for (const selectedDate of selectedMonths) {
            for (const noSelectedDate of unSelectedMonths) {
                if (selectedDate > noSelectedDate) {
                    toast.error("Some previous months not paid");
                    return false;
                }
            }
        }
        return true;
    };

    const handleSubmit = async (values) => {
        if (monthValidation(values)) {
            spinService.on();
            const isNewReceiptHeader = values.id === undefined;
            const url = isNewReceiptHeader
                ? EntityConstants.RECEIPT_HEADERS
                : EntityConstants.RECEIPT_HEADERS + `/${values.id}`;
            const method = isNewReceiptHeader ? post : put;
            const response = await method(url, { receipt_header: values });
            if ([200, 201].indexOf(response.status) > -1) {
                const data = await response.json();
                toast.success(
                    isNewReceiptHeader
                        ? MessageContants.RECEIPT_HEADER_CREATED
                        : MessageContants.RECEIPT_HEADER_UPDATED
                );
                receiptHeaderChangeHandler("id", data.id);
            }
            spinService.off();
        }
    };

    // Initial State based on props id , schoolId, and studentId
    useEffect(() => {
        const fetchReceiptHeader = async () => {
            if (id !== undefined) {
                const response = await get(
                    EntityConstants.RECEIPT_HEADERS + `/${Number(id)}`
                );
                const data = await response.json();
                setSchema(formConfig[data.fee_type].schema);
                setFormFields(formConfig[data.fee_type].formFields);
                setReceiptHeader(data);
                setReceiptLines(data.receipt_lines);
                spinService.off();
            } else {
                setSchema(formConfig["School Transport"].schema);
                setFormFields(formConfig["School Transport"].formFields);
            }
        };
        fetchReceiptHeader();
    }, [id]);

    // Load the default items
    useEffect(() => {
        const fetchDefaultLists = async () => {
            let requests = [
                get(EntityConstants.SCHOOLSBYCOMPANIES),
                get(EntityConstants.ACCOUNTS, "max=all"),
                get(EntityConstants.TRANSPORT_TYPES, "max=all"),
                get(
                    EntityConstants.COMMON_CODES,
                    `types=fee_types,cheque_types,debit_ledgers,voucher_types`
                ),
                get(EntityConstants.MODE_OF_PAYES, `max=all`),
                get(EntityConstants.BANK_DETAILS, `max=all`),
                get(EntityConstants.ACADEMIC_YEARS, "max=all"),
            ];
            const responses = await Promise.all(requests);
            const [
                schools,
                accounts,
                transport_types,
                common_codes,
                mode_of_pays,
                bank_details,
                academic_years
            ] = await Promise.all(responses.map((response) => response.json()));
            setDependencies((prevState) => {
                return {
                    ...prevState,
                    fee_types: common_codes.fee_types,
                    cheque_types: common_codes.cheque_types,
                    debit_ledgers: common_codes.debit_ledgers,
                    voucher_types: common_codes.voucher_types,
                    transport_types: transport_types,
                    accounts: accounts,
                    schools: schools,
                    mode_of_pays: mode_of_pays,
                    bank_details: bank_details,
                    academic_years: academic_years
                };
            });
        };
        receipt_header.fee_type !== undefined && fetchDefaultLists();
    }, []);

    // Active Invoices
    useEffect(() => {
        const fetchActiveInvoice = async (student_id) => {
            const response = await get(
                EntityConstants.INVOICE_HEADERS,
                `max=all&student_id=${student_id}`
            );
            const invoice_headers = await response.json();
            setDependencies((prevState) => {
                return { ...prevState, invoice_headers: invoice_headers };
            });
        };
        receipt_header.fee_type === "Other Fees" &&
            receipt_header.student_id &&
            fetchActiveInvoice(receipt_header.student_id);
    }, [receipt_header.fee_type, receipt_header.student_id]);

    // State, Schema, and FormFields change based on fee type
    useEffect(() => {
        const updateFeeTypeHandler = (fee_type) => {
            setFormFields(formConfig[fee_type].formFields);
            setSchema(formConfig[fee_type].schema);
            let newState = formConfig[fee_type].state;
            if (
                ["School Transport", "Other Fees"].indexOf(receipt_header.fee_type) > -1
            ) {
                if (receipt_header.school_id) {
                    newState.school_id = receipt_header.school_id;
                }
                if (receipt_header.student_id) {
                    newState.student_id = receipt_header.student_id;
                }
                if (receipt_header.mode_of_pay_id) {
                    newState.mode_of_pay_id = receipt_header.mode_of_pay_id;
                }
            }
            setReceiptHeader(newState);
        };
        receipt_header.fee_type === undefined
            ? updateFeeTypeHandler("School Transport")
            : updateFeeTypeHandler(receipt_header.fee_type);
    }, [receipt_header.fee_type]);

    // Fetch Students, Academic Years, Classes, and Zones based on school_id
    useEffect(() => {
        const fetchStudentsAndAcademicYears = async (school_id) => {
            let requests = [
                get(EntityConstants.STUDENTS, `max=all&school_id=${school_id}`),
                get(EntityConstants.GRADES, `max=all&school_id=${school_id}`),
                get(EntityConstants.ZONES_BY_SCHOOL + `/${school_id}`),
            ];
            const responses = await Promise.all(requests);
            const [students, classes, zones] = await Promise.all(
                responses.map((response) => response.json())
            );
            setDependencies((prevState) => {
                return {
                    ...prevState,
                    students: students,
                    classes: classes,
                    zones: zones,
                };
            });
        };
        receipt_header.school_id &&
            fetchStudentsAndAcademicYears(receipt_header.school_id);
    }, [receipt_header.school_id]);

    // Load Areas
    useEffect(() => {
        const fetchAreas = async (zone_id) => {
            const response = await get(EntityConstants.AREAS_BY_ZONE + `/${zone_id}`);
            const areas = await response.json();
            setDependencies((prevState) => {
                return { ...prevState, areas: areas };
            });
        };
        receipt_header.zone_id && fetchAreas(receipt_header.zone_id);
    }, [receipt_header.zone_id]);

    // Load Terms
    useEffect(() => {
        const fetchTermsBySchoolAndAcademicYear = async (
            school_id,
            academic_year_id
        ) => {
            const response = await get(
                EntityConstants.TERMS,
                `school_id=${school_id}&academic_year_id=${academic_year_id}`
            );
            const terms = await response.json();
            setDependencies((prevState) => {
                return { ...prevState, terms: terms };
            });
            let term = terms.find(
                (term) =>
                    new Date(term.start_date) <= new Date() &&
                    new Date(term.end_date) >= new Date()
            );
            if (term && term.id) {
                receiptHeaderChangeHandler("term_id", term.id);
            } else {
                receiptHeaderChangeHandler("term_id", null);
            }
        };
        receipt_header.school_id &&
            receipt_header.academic_year_id &&
            fetchTermsBySchoolAndAcademicYear(
                receipt_header.school_id,
                receipt_header.academic_year_id
            );
    }, [receipt_header.school_id, receipt_header.academic_year_id]);

    // Load Unpaid months
    useEffect(() => {
        const fetchUnpaidMonths = async (school_id, student_id, term_id) => {
            const response = await get(
                `${EntityConstants.UNPAID_INVOICE_MONTHS}/${school_id}/${student_id}/${term_id}`
            );
            const unpaid_months = await response.json();
            setDependencies((prevState) => {
                return { ...prevState, unpaid_months: unpaid_months };
            });
        };
        receipt_header.school_id &&
            receipt_header.student_id &&
            receipt_header.term_id &&
            fetchUnpaidMonths(
                receipt_header.school_id,
                receipt_header.student_id,
                receipt_header.term_id
            );
    }, [
        receipt_header.school_id,
        receipt_header.student_id,
        receipt_header.term_id,
    ]);

    // Set Class, Division, Student Name, Other Ref, Parent Name, Debit Ledger Id Based on selected Student
    useEffect(() => {
        const setFewAttributeValues = (student_id) => {
            const student = dependencies.students.find(
                (stu) => stu.id === student_id
            );
            student &&
                setReceiptHeader((prevState) => {
                    return {
                        ...prevState,
                        class_id: student.class_id,
                        credit_ledger_id: student.id,
                    };
                });
        };
        const fetchActiveTransportByStudent = async (student_id) => {
            const response = await get(
                EntityConstants.STUDENT_TRANSPORT_DETAIL_BY_STUDENT + `/${student_id}`
            );
            const atd = await response.json();
            atd &&
                setReceiptHeader((prevState) => {
                    return {
                        ...prevState,
                        zone_id: atd.zone_id,
                        area_id: atd.area_id,
                        transport_type_id: atd.transport_type_id,
                    };
                });
        };
        receipt_header.fee_type === "School Transport" &&
            receipt_header.student_id &&
            setFewAttributeValues(receipt_header.student_id);
        receipt_header.student_id &&
            receipt_header.fee_type === "School Transport" &&
            fetchActiveTransportByStudent(receipt_header.student_id);
    }, [
        receipt_header.student_id,
        dependencies.students,
        receipt_header.fee_type,
    ]);

    useEffect(() => {
        const fetchRespectiveAcademicYear = async () => {
            const response = await get(`${EntityConstants.ACADEMIC_YEAR_BY_TYPE}/Receipt/${receipt_header.student_id}/${receipt_header.id}`);
            const data = await response.json();
            setReceiptHeader((prevState) => {
                return {
                    ...prevState,
                    academic_year_id: data.academic_year_id,
                };
            });
        };
        receipt_header.student_id && fetchRespectiveAcademicYear();
    }, [receipt_header.student_id, receipt_header.id]);

    // Outstanding Balance
    useEffect(() => {
        const fetchOutstandingBalance = async (student_id, term_id) => {
            const response = await get(
                EntityConstants.STUDENT_OUTSTANDING_BALANCE +
                `/${student_id}/${term_id}`
            );
            const data = await response.json();
            receiptHeaderChangeHandler(
                "outstanding_balance",
                data.outstanding_balance
            );
        };
        receipt_header.student_id &&
            receipt_header.fee_type === "School Transport" &&
            fetchOutstandingBalance(
                receipt_header.student_id,
                receipt_header.term_id
            );
    }, [
        receipt_header.student_id,
        receipt_header.term_id,
        receipt_header.fee_type,
        receiptLines,
    ]);

    // Attach Debit ledgers by mode of pay
    useEffect(() => {
        const updateDebitLedgerList = (mode_of_pay_id) => {
            const selectedModeOfPay = dependencies.mode_of_pays.find(
                (item) => item.id === mode_of_pay_id
            );
            if (
                selectedModeOfPay &&
                selectedModeOfPay.mode_of_pay === "Bank Transfer"
            ) {
                attachOptions(
                    "debit_ledger_id",
                    dependencies.debit_ledgers.filter(
                        (item) =>
                            ["Bank-Mashreq-G1", "Bank- Rak Bank G1"].indexOf(item.code) > -1
                    )
                );
            } else {
                attachOptions("debit_ledger_id", dependencies.debit_ledgers);
            }

            const requiredFileds =
                selectedModeOfPay !== undefined &&
                requiredFormFieldMapper[selectedModeOfPay.mode_of_pay];
            requiredFileds &&
                requiredFileds.map((fn) => {
                    if (schema && schema.fields && schema.fields[fn]) {
                        makeRequired(fn, true);
                        schema.fields[fn] = schema.fields[fn].required();
                        return setSchema(schema);
                    }
                });
            const disabledFields =
                selectedModeOfPay !== undefined &&
                disabledFormFieldMapper[selectedModeOfPay.mode_of_pay];
            disabledFields &&
                disabledFields.map((fn) => {
                    if (schema && schema.fields && schema.fields[fn]) {
                        makeRequired(fn, false);
                        makeDisabled(fn, true);
                        schema.fields[fn] = schema.fields[fn].nullable();
                        return setSchema(schema);
                    }
                });
            !disabledFields &&
                notDisabled.map((fn) => {
                    if (schema && schema.fields && schema.fields[fn]) {
                        makeDisabled(fn, false);
                        schema.fields[fn] = schema.fields[fn].nullable();
                        return setSchema(schema);
                    }
                });
        };
        receipt_header.mode_of_pay_id &&
            updateDebitLedgerList(receipt_header.mode_of_pay_id);
    }, [receipt_header.mode_of_pay_id, dependencies.debit_ledgers]);

    // Attach schools as option
    useEffect(() => {
        if (
            ["School Transport", "Other Fees"].indexOf(receipt_header.fee_type) > -1
        ) {
            attachOptions("school_id", dependencies.schools);
            if (studentId !== undefined && schoolId !== undefined) {
                setReceiptHeader((prevState) => {
                    return { ...prevState, school_id: schoolId, student_id: studentId };
                });
            }
        }
    }, [dependencies.schools, receipt_header.fee_type]);

    // Attach fee types as option
    useEffect(() => {
        attachOptions("fee_type", dependencies.fee_types);
    }, [dependencies.fee_types, receipt_header.fee_type]);

    // Attach cheque_types as option
    useEffect(() => {
        if (receipt_header.fee_type !== "PDC Bank Receipt") {
            attachOptions("cheque_type", dependencies.cheque_types);
        }
    }, [dependencies.cheque_types, receipt_header.fee_type]);

    // Attach debit_ledgers as option
    useEffect(() => {
        if (receipt_header.fee_type === "PDC Bank Receipt") {
            attachOptions("debit_ledger_id", dependencies.accounts);
        }
        if (receipt_header.fee_type !== "PDC Bank Receipt") {
            attachOptions("debit_ledger_id", dependencies.debit_ledgers);
        }
    }, [
        dependencies.debit_ledgers,
        dependencies.accounts,
        receipt_header.fee_type,
    ]);

    // Attach voucher_types as option
    useEffect(() => {
        attachOptions("voucher_type", dependencies.voucher_types);
    }, [dependencies.voucher_types]);

    // Attach transport types as option
    useEffect(() => {
        if (receipt_header.fee_type === "School Transport") {
            attachOptions("transport_type_id", dependencies.transport_types);
        }
    }, [dependencies.transport_types, receipt_header.fee_type]);

    // Attach academic years as option
    useEffect(() => {
        attachOptions("academic_year_id", dependencies.academic_years);
        // dependencies.academic_years.length > 0 &&
        //     receiptHeaderChangeHandler(
        //         "academic_year_id",
        //         dependencies.academic_years[0].id
        //     );
    }, [dependencies.academic_years, receipt_header.fee_type]);

    // Attach accounts as option
    useEffect(() => {
        if (receipt_header.fee_type === "School Transport") {
            attachOptions("credit_ledger_id", dependencies.students);
        }
        if (receipt_header.fee_type !== "School Transport") {
            attachOptions("credit_ledger_id", dependencies.accounts);
        }
    }, [dependencies.accounts, dependencies.students, receipt_header.fee_type]);

    // Attach mode_of_pays as option
    useEffect(() => {
        if (
            ["School Transport", "Other Fees"].indexOf(receipt_header.fee_type) > -1
        ) {
            attachOptions("mode_of_pay_id", dependencies.mode_of_pays);
        }
    }, [dependencies.mode_of_pays, receipt_header.fee_type]);

    // Attach bank_details as option
    useEffect(() => {
        if (
            ["School Transport", "Other Fees"].indexOf(receipt_header.fee_type) > -1
        ) {
            attachOptions("bank_detail_id", dependencies.bank_details);
        }
    }, [dependencies.bank_details, receipt_header.fee_type]);

    // Attach invoice_headers as option
    useEffect(() => {
        if (receipt_header.fee_type === "Other Fees") {
            attachOptions("invoice_header_id", dependencies.invoice_headers);
        }
    }, [dependencies.invoice_headers, receipt_header.fee_type]);

    // Attach students as option
    useEffect(() => {
        if (
            ["School Transport", "Other Fees"].indexOf(receipt_header.fee_type) > -1
        ) {
            attachOptions("student_id", dependencies.students);
        }
    }, [dependencies.students, receipt_header.fee_type]);

    // Attach invoice_headers as option
    useEffect(() => {
        if (receipt_header.fee_type === "School Transport") {
            attachOptions("class_id", dependencies.classes);
        }
    }, [dependencies.classes, receipt_header.fee_type]);

    // Attach invoice_headers as option
    useEffect(() => {
        if (receipt_header.fee_type === "School Transport") {
            attachOptions("zone_id", dependencies.zones);
        }
    }, [dependencies.zones, receipt_header.fee_type]);

    // Attach areas as option
    useEffect(() => {
        if (receipt_header.fee_type === "School Transport") {
            attachOptions("area_id", dependencies.areas);
        }
    }, [dependencies.areas, receipt_header.fee_type]);

    // Attach terms as option
    useEffect(() => {
        if (receipt_header.fee_type === "School Transport") {
            attachOptions("term_id", dependencies.terms);
        }
    }, [dependencies.terms, receipt_header.school_id, receipt_header.fee_type]);

    // Attach Unpaid months
    useEffect(() => {
        let rh_month =
            receipt_header.months === undefined || receipt_header.months === null
                ? []
                : receipt_header.months;
        if (
            (rh_month.length > 0 || dependencies.unpaid_months.length > 0) &&
            receipt_header.fee_type === "School Transport"
        ) {
            let unpaid_months = dependencies.unpaid_months.map((mon) => mon.month);
            let temp_months = [...unpaid_months, ...rh_month];
            let month_as_options = months.filter((mon) =>
                temp_months.includes(mon.id)
            );
            attachOptions("months", month_as_options);
        }
    }, [
        receipt_header.fee_type,
        dependencies.unpaid_months,
        receipt_header.months,
    ]);

    return (
        <Container>
            <Formik
                enableReinitialize
                initialValues={receipt_header}
                validationSchema={schema}
                onSubmit={(values) => handleSubmit(values)}
            >
                {({ values, touched, errors, handleChange, handleSubmit }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <StyledDrawerForm>
                            <Grid
                                container
                                // maxWidth="md"
                                rowSpacing={1}
                                columnSpacing={{ xs: 1, sm: 2, md: 2 }}
                            >
                                {formFields.map((formField, index) => {
                                    return (
                                        <Grid key={index} item xs={12} sm={12} md={3} lg={3}>
                                            <GoForm
                                                config={formField}
                                                values={values}
                                                touched={touched}
                                                errors={errors}
                                                handleChange={(e) => {
                                                    handleChange(e);
                                                    handleValueChange(e, values);
                                                }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                            <Box sx={{ p: 2, textAlign: "right" }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<Iconify icon="eva:close-circle-outline" />}
                                    onClick={() => onClose()}
                                >
                                    Close
                                </Button>
                                &nbsp;&nbsp;
                                {values.id !== undefined &&
                                    receiptLines.length === 0 &&
                                    permissions.has(PermissionConstants.RECEIPT_DELETE) &&
                                    (values.fee_type === "PDC Bank Receipt" ||
                                        values.fee_type === "School Transport") && (
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="ic:twotone-delete" />}
                                            onClick={() => setConfirm(true)}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                &nbsp;&nbsp;
                                {values.id !== undefined &&
                                    values.fee_type === "PDC Bank Receipt" &&
                                    permissions.has(PermissionConstants.RECEIPT_UPDATE) && (
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="mdi:tick-circle-outline" />}
                                            onClick={() =>
                                                handleStatusUpdateHandler({
                                                    id: values.id,
                                                    status: "Active",
                                                })
                                            }
                                            disabled={receipt_header.status === "Active"}
                                        >
                                            {receipt_header.status === "Active"
                                                ? "Activated"
                                                : "Activate"}
                                        </Button>
                                    )}
                                &nbsp;&nbsp;
                                {((values.id === undefined &&
                                    permissions.has(PermissionConstants.RECEIPT_CREATE) &&
                                    receipt_header.fee_type !== "School Transport") ||
                                    (values.id !== undefined &&
                                        permissions.has(PermissionConstants.RECEIPT_UPDATE)) ||
                                    (values.id === undefined &&
                                        permissions.has(PermissionConstants.RECEIPT_CREATE) &&
                                        receipt_header.fee_type === "School Transport" &&
                                        receipt_header.outstanding_balance > 0)) && (
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="eva:save-fill" />}
                                            type="submit"
                                        >
                                            {values.id === undefined ? "Submit" : "Update"}
                                        </Button>
                                    )}
                                &nbsp;&nbsp;
                                {values.id !== undefined &&
                                    permissions.has(PermissionConstants.RECEIPT_DOWNLOAD) && (
                                        <Button
                                            variant="contained"
                                            startIcon={<Iconify icon="typcn:download" />}
                                            onClick={handlePdfDownloadChange}
                                        >
                                            Download
                                        </Button>
                                    )}
                            </Box>
                        </StyledDrawerForm>
                    </Form>
                )}
            </Formik>
            <Divider />
            <br />
            {(id !== undefined || receipt_header.id !== undefined) && (
                <ReceiptLines
                    feeType={receipt_header.fee_type}
                    receiptHeader={receipt_header}
                    receiptHeaderId={receipt_header.id}
                    receiptLines={receiptLines}
                    outstanding_balance={receipt_header.outstanding_balance}
                    reload={() => fetchReceiptLines(receipt_header.id)}
                />
            )}
            <GoDialog
                open={confirm}
                title=""
                content="Are you sure you want to delete?"
                positive="Yes"
                negative="No"
                handleClose={(res) => handleClose(res)}
            />
        </Container>
    );
};

ReceiptHeader.propTypes = {
    id: PropTypes.number,
    onClose: PropTypes.any,
};

export default ReceiptHeader;
